import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Building2, ChevronRight } from 'lucide-react';
import { projects } from '../mock/data';

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = projects.find(p => p.slug === slug);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Determine which tab this project belongs to
  const getProjectTab = (project) => {
    if (!project) return 'professional';
    
    const isProfessional = 
      project.organization !== 'IIT Kharagpur' && 
      project.organization !== 'Personal Project' &&
      !project.role.includes('Intern') &&
      (project.role.includes('Lead') || project.role.includes('Engineer') || project.role.includes('Consultant'));
    
    return isProfessional ? 'professional' : 'projects';
  };

  const handleBackToWork = () => {
    const tab = getProjectTab(project);
    navigate(`/?tab=${tab}#work`);
    
    // Scroll to work section after navigation
    setTimeout(() => {
      const element = document.getElementById('work');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl text-[#1A1A1A] mb-4">Project Not Found</h1>
          <Link to="/" className="text-[#4A5568] hover:text-[#1A1A1A] transition-colors duration-300">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // If project doesn't have fullDescription, show simple card
  if (!project.fullDescription) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl text-[#1A1A1A] mb-4">{project.title}</h1>
          <p className="text-lg text-[#6B6B6B] mb-8">{project.description}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors">
            <ArrowLeft size={16} />
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  // Unified Sidebar Layout for all full projects
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/20 pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Back Navigation */}
        <button
          onClick={handleBackToWork}
          className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors duration-300 mb-8"
        >
          <ArrowLeft size={16} />
          Back to Work
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Sidebar - Fixed Metadata */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 space-y-8">
              {/* Project Metadata Card */}
              <div className="bg-white rounded-lg p-8 shadow-sm border border-teal-100">
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full mb-4">
                      {project.category}
                    </span>
                    <h1 className="font-serif text-2xl text-[#1A1A1A] leading-tight">
                      {project.title}
                    </h1>
                  </div>

                  {/* Structured Facts */}
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 text-sm">
                      <Building2 size={16} className="text-teal-600 flex-shrink-0" />
                      <p className="text-[#4A4A4A] font-medium">{project.organization}</p>
                    </div>
                    
                    <div className="flex items-center gap-3 text-sm">
                      <Calendar size={16} className="text-teal-600 flex-shrink-0" />
                      <p className="text-[#4A4A4A] font-medium">{project.period}</p>
                    </div>

                    {project.location && (
                      <div className="flex items-center gap-3 text-sm">
                        <MapPin size={16} className="text-teal-600 flex-shrink-0" />
                        <p className="text-[#4A4A4A] font-medium">{project.location}</p>
                      </div>
                    )}
                  </div>

                  {/* Role */}
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-xs text-[#999999] uppercase tracking-wide mb-2">Role</p>
                    <p className="text-sm text-[#4A4A4A] font-medium">{project.role}</p>
                  </div>

                  {/* Tags */}
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-xs text-[#999999] uppercase tracking-wide mb-3">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs px-2.5 py-1 bg-gray-50 text-[#6B6B6B] rounded border border-gray-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Tech Stack */}
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-xs text-[#999999] uppercase tracking-wide mb-3">Tech Stack</p>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="text-xs px-2.5 py-1 bg-teal-50 text-teal-700 rounded font-mono"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Navigation */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <p className="text-xs font-semibold text-[#1A1A1A] uppercase tracking-wide mb-4">Quick Navigation</p>
                <div className="space-y-2">
                  <a href="#overview" className="flex items-center justify-between text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors py-2">
                    <span>Overview</span>
                    <ChevronRight size={14} />
                  </a>
                  <a href="#challenge" className="flex items-center justify-between text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors py-2">
                    <span>Challenge</span>
                    <ChevronRight size={14} />
                  </a>
                  <a href="#approach" className="flex items-center justify-between text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors py-2">
                    <span>{project.slug === 'agentic-procurement-platform' ? 'My Role & Responsibilities' : 'Approach'}</span>
                    <ChevronRight size={14} />
                  </a>
                  <a href="#outcomes" className="flex items-center justify-between text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors py-2">
                    <span>Outcomes</span>
                    <ChevronRight size={14} />
                  </a>
                  {project.constraints && (
                    <a href="#constraints" className="flex items-center justify-between text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors py-2">
                      <span>Constraints</span>
                      <ChevronRight size={14} />
                    </a>
                  )}
                  <a href="#learnings" className="flex items-center justify-between text-sm text-[#6B6B6B] hover:text-teal-600 transition-colors py-2">
                    <span>Key Learnings</span>
                    <ChevronRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content Column - Main Narrative */}
          <div className="lg:col-span-8 space-y-12">
            {/* Overview */}
            <section id="overview" className="bg-white rounded-lg p-8 lg:p-10 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-teal-600 rounded-full"></div>
                <h2 className="font-serif text-2xl lg:text-3xl text-[#1A1A1A]">Overview</h2>
              </div>
              <div className="text-base text-[#6B6B6B] leading-relaxed space-y-4">
                {project.slug === 'agentic-procurement-platform' ? (
                  <>
                    <p>Valorant Technology is a procurement-focused consulting firm expanding from services into product-led offerings. To support this transition, the company initiated Astra, a unified, AI-powered procurement platform for Intake Management and Spend Analytics.</p>
                    <p>I joined Valorant Technology as the <strong>first technical hire</strong>, with a mandate to <strong>conceptualize, architect, and deliver the technical foundation</strong> for this next-generation, agentic procurement platform.</p>
                  </>
                ) : (
                  <p>{project.fullDescription}</p>
                )}
              </div>
            </section>

            {/* Challenge */}
            <section id="challenge" className="bg-white rounded-lg p-8 lg:p-10 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-amber-600 rounded-full"></div>
                <h2 className="font-serif text-2xl lg:text-3xl text-[#1A1A1A]">Challenge</h2>
              </div>
              <div className="text-base text-[#6B6B6B] leading-relaxed space-y-4">
                {project.slug === 'agentic-procurement-platform' ? (
                  <>
                    {/* Intro paragraph */}
                    <p className="text-base text-[#6B6B6B] leading-relaxed mb-6">
                      Building Astra as a first-of-its-kind, agentic procurement platform involved navigating several foundational challenges that shaped both the technical and product strategy:
                    </p>
                    {/* Numbered challenges */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                          1
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-[#1A1A1A] mb-2">Fragmented & Manual Procurement Ecosystems</p>
                          <p className="text-base text-[#6B6B6B] leading-relaxed">Traditional procurement processes across organizations were highly fragmented, manual, and time-consuming. Core activities such as vendor management, quote comparison, approvals, and workflow orchestration were often handled through disconnected tools, emails, and spreadsheets, creating inefficiencies and limited visibility.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                          2
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-[#1A1A1A] mb-2">Highly Variable, Organization-Specific Workflows</p>
                          <p className="text-base text-[#6B6B6B] leading-relaxed">No two organizations followed the same procurement workflow. Each had unique approval hierarchies, compliance rules, and intake patterns, requiring the platform to be <strong>deeply configurable and workflow-agnostic</strong> rather than hard-coded for a single process.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                          3
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-[#1A1A1A] mb-2">Ambiguity Around "Agentic AI" Definitions</p>
                          <p className="text-base text-[#6B6B6B] leading-relaxed">The problem space itself was ambiguous. Stakeholders had varying interpretations of what "agentic" meant, ranging from simple automation to fully autonomous systems. There was no shared mental model, making it necessary to define <strong>clear agent boundaries, human-in-the-loop controls, and realistic autonomy levels</strong> from first principles.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                          4
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-[#1A1A1A] mb-2">Zero-to-One Product Creation</p>
                          <p className="text-base text-[#6B6B6B] leading-relaxed">There were no existing tools, codebases, or reference architectures to build upon. Everything from ideation and system design to roadmap planning and productionization had to be created from scratch while still aligning with enterprise procurement realities.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                          5
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-[#1A1A1A] mb-2">Absence of an Initial Engineering Team</p>
                          <p className="text-base text-[#6B6B6B] leading-relaxed">As the first technical hire, I operated without an existing engineering team or established processes. This required simultaneously acting as architect, implementer, and technical leader while laying down engineering standards, documentation practices, and a scalable foundation for future hires.</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <p>{project.challenge}</p>
                )}
              </div>
            </section>

            {/* Approach / Role & Responsibilities */}
            <section id="approach" className="bg-white rounded-lg p-8 lg:p-10 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-cyan-600 rounded-full"></div>
                <h2 className="font-serif text-2xl lg:text-3xl text-[#1A1A1A]">
                  {project.slug === 'agentic-procurement-platform' ? 'My Role & Responsibilities' : 'Approach'}
                </h2>
              </div>
              <div className="space-y-4">
                {project.slug === 'agentic-procurement-platform' ? (
                  <>
                    {/* Intro paragraph */}
                    <p className="text-base text-[#6B6B6B] leading-relaxed mb-6">
                      {project.approach[0]}
                    </p>
                    {/* Numbered responsibilities */}
                    {project.approach.slice(1).map((item, index) => {
                      if (typeof item === 'object' && item.title && item.description) {
                        return (
                          <div key={index} className="space-y-2">
                            <div className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                                {index + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-semibold text-[#1A1A1A] mb-2">{item.title}</p>
                                <p className="text-base text-[#6B6B6B] leading-relaxed">{item.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </>
                ) : (
                  project.approach.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                        {index + 1}
                      </span>
                      <p className="text-base text-[#6B6B6B] leading-relaxed">{item}</p>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Outcomes & Impact */}
            <section id="outcomes" className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-8 lg:p-10 border border-teal-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-teal-600 rounded-full"></div>
                <h2 className="font-serif text-2xl lg:text-3xl text-[#1A1A1A]">Outcomes & Impact</h2>
              </div>
              <ul className="space-y-3">
                {project.outcomes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs mt-0.5">✓</span>
                    <span className="text-base text-[#4A4A4A] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Constraints & Trade-offs */}
            {project.constraints && (
              <section id="constraints" className="bg-white rounded-lg p-8 lg:p-10 shadow-sm border-l-4 border-gray-400">
                <h2 className="font-serif text-2xl lg:text-3xl text-[#1A1A1A] mb-6">Constraints & Trade-offs</h2>
                <p className="text-base text-[#6B6B6B] leading-relaxed">
                  {project.constraints}
                </p>
              </section>
            )}

            {/* Key Learnings */}
            <section id="learnings" className="bg-white rounded-lg p-8 lg:p-10 shadow-sm border-l-4 border-teal-600">
              <h2 className="font-serif text-2xl lg:text-3xl text-[#1A1A1A] mb-6">Key Learnings</h2>
              <p className="text-base text-[#6B6B6B] leading-relaxed italic">
                {project.learnings}
              </p>
            </section>

            {/* Back to Work Button */}
            <div className="flex justify-center pt-8">
              <button
                onClick={handleBackToWork}
                className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 font-semibold shadow-md hover:shadow-lg"
              >
                <ArrowLeft size={20} />
                Back to Selected Work
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
